import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  CreateKnowledgeSessionRequest,
  KnowledgeApi,
  KnowledgeSessionResponse,
  SendKnowledgeMessageResponse,
} from './KnowledgeApi';
import type {
  KnowledgeCapabilities,
  KnowledgeBuilderTemplateParameters,
  KnowledgeContextFocus,
  KnowledgeDocument,
  KnowledgeMessageAttachment,
  KnowledgeSession,
  KnowledgeSource,
  KnowledgeValidationResult,
  ProposedDraftChange,
  DefinitionFieldState,
  DefinitionFieldStatus,
  DefinitionFinding,
  DefinitionStateResponse,
} from './types';

export class KnowledgeClient implements KnowledgeApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getConfig(): Promise<KnowledgeCapabilities> {
    return this.request('/config');
  }

  async createSession(
    request: CreateKnowledgeSessionRequest,
  ): Promise<KnowledgeSessionResponse> {
    return this.request('/sessions', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getSession(sessionId: string): Promise<KnowledgeSessionResponse> {
    return this.request(`/sessions/${encodeURIComponent(sessionId)}`);
  }

  async updateSessionContext(
    sessionId: string,
    request: {
      focus?: KnowledgeContextFocus;
      templateParameters?: KnowledgeBuilderTemplateParameters;
    },
  ): Promise<KnowledgeSession> {
    const response = await this.request<{ session: KnowledgeSession }>(
      `/sessions/${encodeURIComponent(sessionId)}/context`,
      {
        method: 'PATCH',
        body: JSON.stringify(request),
      },
    );
    return response.session;
  }

  async sendMessage(
    sessionId: string,
    content: string,
    activeDocumentPath?: string,
    agent?: CreateKnowledgeSessionRequest['agent'],
    signal?: AbortSignal,
    attachments?: KnowledgeMessageAttachment[],
  ): Promise<SendKnowledgeMessageResponse> {
    return this.request(`/sessions/${encodeURIComponent(sessionId)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, activeDocumentPath, agent, attachments }),
      signal,
    });
  }

  async failActiveRuns(sessionId: string, error?: string): Promise<void> {
    await this.request(
      `/sessions/${encodeURIComponent(sessionId)}/runs/fail-active`,
      {
        method: 'POST',
        body: JSON.stringify({ error }),
      },
    );
  }

  async listProposedChanges(
    sessionId: string,
  ): Promise<ProposedDraftChange[]> {
    const response = await this.request<{
      proposedChanges: ProposedDraftChange[];
    }>(`/sessions/${encodeURIComponent(sessionId)}/proposed-changes`);
    return response.proposedChanges;
  }

  async approveProposedChange(
    sessionId: string,
    changeId: string,
    operationIndex?: number,
  ): Promise<{
    change: ProposedDraftChange;
    updatedDocuments: KnowledgeDocument[];
  }> {
    return this.request(
      `/sessions/${encodeURIComponent(
        sessionId,
      )}/proposed-changes/${encodeURIComponent(changeId)}/approve`,
      { method: 'POST', body: JSON.stringify({ operationIndex }) },
    );
  }

  async rejectProposedChange(
    sessionId: string,
    changeId: string,
    operationIndex?: number,
  ): Promise<ProposedDraftChange> {
    const response = await this.request<{ change: ProposedDraftChange }>(
      `/sessions/${encodeURIComponent(
        sessionId,
      )}/proposed-changes/${encodeURIComponent(changeId)}/reject`,
      { method: 'POST', body: JSON.stringify({ operationIndex }) },
    );
    return response.change;
  }

  async updateDocument(
    sessionId: string,
    documentId: string,
    content: string,
  ): Promise<KnowledgeDocument> {
    const response = await this.request<{ document: KnowledgeDocument }>(
      `/sessions/${encodeURIComponent(
        sessionId,
      )}/documents/${encodeURIComponent(documentId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ content }),
      },
    );
    return response.document;
  }

  async createDocument(
    sessionId: string,
    request: { path: string; title?: string; content?: string },
  ): Promise<KnowledgeDocument> {
    const response = await this.request<{ document: KnowledgeDocument }>(
      `/sessions/${encodeURIComponent(sessionId)}/documents`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
    return response.document;
  }

  async renameDocument(
    sessionId: string,
    documentId: string,
    path: string,
  ): Promise<KnowledgeDocument> {
    const response = await this.request<{ document: KnowledgeDocument }>(
      `/sessions/${encodeURIComponent(
        sessionId,
      )}/documents/${encodeURIComponent(documentId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ path }),
      },
    );
    return response.document;
  }

  async renameFolder(
    sessionId: string,
    oldPath: string,
    newPath: string,
  ): Promise<KnowledgeDocument[]> {
    const response = await this.request<{ documents: KnowledgeDocument[] }>(
      `/sessions/${encodeURIComponent(sessionId)}/folders`,
      {
        method: 'PATCH',
        body: JSON.stringify({ oldPath, newPath }),
      },
    );
    return response.documents;
  }

  async deleteDocument(sessionId: string, documentId: string): Promise<void> {
    await this.request(
      `/sessions/${encodeURIComponent(
        sessionId,
      )}/documents/${encodeURIComponent(documentId)}`,
      { method: 'DELETE' },
    );
  }

  async deleteFolder(sessionId: string, path: string): Promise<void> {
    await this.request(`/sessions/${encodeURIComponent(sessionId)}/folders`, {
      method: 'DELETE',
      body: JSON.stringify({ path }),
    });
  }

  async acceptDocument(
    sessionId: string,
    documentId: string,
  ): Promise<KnowledgeDocument> {
    const response = await this.request<{ document: KnowledgeDocument }>(
      `/sessions/${encodeURIComponent(
        sessionId,
      )}/documents/${encodeURIComponent(documentId)}/accept`,
      { method: 'POST' },
    );
    return response.document;
  }

  async revertDocument(
    sessionId: string,
    documentId: string,
  ): Promise<KnowledgeDocument> {
    const response = await this.request<{ document: KnowledgeDocument }>(
      `/sessions/${encodeURIComponent(
        sessionId,
      )}/documents/${encodeURIComponent(documentId)}/revert`,
      { method: 'POST' },
    );
    return response.document;
  }

  async addUrl(
    sessionId: string,
    request: { title?: string; url: string },
  ): Promise<KnowledgeSource> {
    const response = await this.request<{ source: KnowledgeSource }>(
      `/sessions/${encodeURIComponent(sessionId)}/sources/urls`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
    return response.source;
  }

  async uploadFile(sessionId: string, file: File): Promise<KnowledgeSource> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await this.request<{ source: KnowledgeSource }>(
      `/sessions/${encodeURIComponent(sessionId)}/sources/files`,
      {
        method: 'POST',
        body: formData,
      },
      false,
    );
    return response.source;
  }

  async removeSource(sessionId: string, sourceId: string): Promise<void> {
    await this.request(
      `/sessions/${encodeURIComponent(sessionId)}/sources/${encodeURIComponent(
        sourceId,
      )}`,
      { method: 'DELETE' },
    );
  }

  async validateSession(sessionId: string): Promise<KnowledgeValidationResult> {
    return this.request(`/sessions/${encodeURIComponent(sessionId)}/validate`, {
      method: 'POST',
    });
  }

  async getDefinitionState(
    sessionId: string,
  ): Promise<DefinitionStateResponse> {
    return this.request(
      `/sessions/${encodeURIComponent(sessionId)}/definition`,
    );
  }

  async sendInterviewMessage(
    sessionId: string,
    content: string,
    activeDocumentPath?: string,
    agent?: CreateKnowledgeSessionRequest['agent'],
    signal?: AbortSignal,
  ) {
    return this.request<
      SendKnowledgeMessageResponse & {
        definitionUpdates: DefinitionFieldState[];
        definitionState?: DefinitionStateResponse;
      }
    >(`/sessions/${encodeURIComponent(sessionId)}/definition/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, activeDocumentPath, agent }),
      signal,
    });
  }

  async setDefinitionFieldStatus(
    sessionId: string,
    sectionId: string,
    fieldId: string,
    status: DefinitionFieldStatus,
    value?: string,
  ): Promise<DefinitionFieldState> {
    const response = await this.request<{ field: DefinitionFieldState }>(
      `/sessions/${encodeURIComponent(
        sessionId,
      )}/definition/fields/${encodeURIComponent(fieldId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ sectionId, status, value }),
      },
    );
    return response.field;
  }

  async runDefinitionReview(
    sessionId: string,
  ): Promise<{ findings: DefinitionFinding[]; message: string }> {
    return this.request(
      `/sessions/${encodeURIComponent(sessionId)}/definition/review`,
      { method: 'POST' },
    );
  }

  private async request<T>(
    path: string,
    init: RequestInit = {},
    json = true,
  ): Promise<T> {
    const discoveredBaseUrl = await this.discoveryApi.getBaseUrl('knowledge');
    const baseUrl =
      discoveredBaseUrl && discoveredBaseUrl !== '/'
        ? discoveredBaseUrl
        : '/api/knowledge';
    if (baseUrl !== discoveredBaseUrl) {
      console.warn('[KnowledgeClient] Discovery returned empty base URL', {
        discoveredBaseUrl,
        fallbackBaseUrl: baseUrl,
      });
    }
    const headers = new Headers(init.headers);
    if (json && init.body && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers,
    });

    if (!response.ok) {
      const message = await readErrorMessage(response);
      console.error('[KnowledgeClient] Request failed', {
        path,
        status: response.status,
        message,
      });
      throw new Error(
        message || `Knowledge request failed: ${response.status}`,
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }
}

async function readErrorMessage(response: Response) {
  const text = await response.text();
  if (!text) {
    return '';
  }
  try {
    const parsed = JSON.parse(text) as { error?: { message?: string } };
    return parsed.error?.message ?? text;
  } catch {
    return text;
  }
}
